import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { SectionNodeView } from './view';
import type { ExtensionMenuConfig } from '@cotera/client/app/components/app/rte/types/extension-config';

export interface SectionOptions extends ExtensionMenuConfig {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    section: {
      /**
       * Insert a section block
       */
      setSection: (attributes?: {
        color?: string;
        title?: string;
      }) => ReturnType;
      /**
       * Convert current block into a section
       */
      turnIntoSection: (attributes?: {
        color?: string;
        title?: string;
      }) => ReturnType;
    };
  }
}

export const Section = Node.create<SectionOptions>({
  name: 'section',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: 'block',

  content: 'block+',

  addAttributes() {
    return {
      color: {
        default: 'blue',
        rendered: false,
      },
      title: {
        default: '',
        rendered: false,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="section"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['react-component', mergeAttributes(HTMLAttributes), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(SectionNodeView);
  },

  addCommands() {
    return {
      setSection:
        (attributes = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: attributes,
            content: [
              {
                type: 'paragraph',
                content: [],
              },
            ],
          });
        },
      turnIntoSection:
        (attributes = {}) =>
        ({ commands: _commands, state, chain }) => {
          const { selection } = state;
          const { $from } = selection;
          const currentNode = $from.node($from.depth);

          // Extract text content from current node
          const textContent = currentNode.textContent;

          // Delete current node and insert section with text content wrapped in paragraph
          return chain()
            .focus()
            .deleteSelection()
            .insertContent({
              type: this.name,
              attrs: attributes,
              content: [
                {
                  type: 'paragraph',
                  content: textContent
                    ? [{ type: 'text', text: textContent }]
                    : [],
                },
              ],
            })
            .run();
        },
    };
  },
});
